import React, { createContext, useState, useContext, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";

import PayPalButton from "core/PayPalButton";

import { REGISTER_PAYPAL_SUBSCRIPTION } from "./mutations";
import { GET_SUBSCRIPTION_PLAN_LIST } from "./queries";

const Context = createContext();

import { useUpdateBillingInfo } from "frontend/context/CheckoutContext";

const PayPalNounProSubProvider = ({ children }) => {
    /* --------------------------------- STATES --------------------------------- */

    const [upgradeSuccessful, setUpgradeSuccessful] = useState(false);
    const [error, setError] = useState("");
    const { submitBillingInfo } = useUpdateBillingInfo();

    /* -------------------------------- REQUESTS -------------------------------- */

    const [
        registerPayPalSubscription,
        { data: registrationData }
    ] = useMutation(REGISTER_PAYPAL_SUBSCRIPTION);

    const { data: planListData } = useQuery(GET_SUBSCRIPTION_PLAN_LIST, {
        variables: { paymentProvider: "PAYPAL" }
    });

    /* ------------------------ PAYPAL SUBSCRIPTION PLANS ----------------------- */

    const monthlyPlan =
        planListData &&
        planListData.planList.plans.find(plan => plan.interval === "MONTH");

    const yearlyPlan =
        planListData &&
        planListData.planList.plans.find(plan => plan.interval === "YEAR");

    /* ---------------------- PAYPAL SUBSCRIPTION CREATION ---------------------- */

    const createMonthlySubscription = (data, actions) =>
        actions.subscription.create({
            plan_id: monthlyPlan.paypalId
        });

    const createYearlySubscription = (data, actions) =>
        actions.subscription.create({
            plan_id: yearlyPlan.paypalId
        });

    const handleSubscriptionCreationSuccess = async ({ subscriptionID }) => {
        // if a user has VAT/Billing info, we need to update
        // it first or else it will not show up on invoice
        // updating billing info should always be non blocking but needs
        // to happen first - so isSuccess always is true
        const isSuccess = await submitBillingInfo();

        if (isSuccess) {
            registerPayPalSubscription({
                variables: {
                    subscriptionID
                }
            });
        }
    };

    const handleSubscriptionCreationError = error => {
        setError(error.message);
    };

    /* ----------------- REGISTER PAYPAL SUBSCRIPTION IN BACKEND ---------------- */

    useEffect(() => {
        if (registrationData) {
            if (registrationData.registerPayPalSubscription.ok) {
                setUpgradeSuccessful(true);
            }

            if (!registrationData.registerPayPalSubscription.ok) {
                setError(registrationData.registerPayPalSubscription.errors);
            }
        }
    }, [registrationData]);

    /* ----------------------------- PURCHASE BUTTON ---------------------------- */

    const buttonProps = {
        onApprove: handleSubscriptionCreationSuccess,
        onError: handleSubscriptionCreationError
    };

    const PurchaseButtonMonthly = props => (
        <PayPalButton
            createSubscription={createMonthlySubscription}
            {...buttonProps}
            {...props}
        />
    );

    const PurchaseButtonYearly = props => (
        <PayPalButton
            createSubscription={createYearlySubscription}
            {...buttonProps}
            {...props}
        />
    );

    /* ----------------------------- CONTEXT VALUES ----------------------------- */

    const contextValues = {
        upgradeSuccessful,
        error,
        PurchaseButtonMonthly,
        PurchaseButtonYearly
    };

    return (
        <Context.Provider value={contextValues}>{children}</Context.Provider>
    );
};

export { PayPalNounProSubProvider };

export const usePayPalNounProSubContext = () => {
    const context = useContext(Context);

    if (context === undefined) {
        throw new Error(
            "usePayPalNounProSubContext must be used within a PayPalNounProSubProvider"
        );
    }

    return context;
};
